import { roundWarehouseQuantity } from '../../utils/warehouseQuantity';

/**
 * Where the goods of a WZ line come from (decision 2026-10-04,
 * docs/decisions/2026-10-04-karta-zk-pelne-fifo-i-proste-transporty.md):
 *  - FIFO:    from the warehouse, oldest batches first (every new line);
 *  - BATCHES: from the batches picked in the „Wystaw WZ” window
 *             (`components/WzIssueModal.tsx`, „Wybiorę partie”);
 *  - TRUCK / MIX: legacy lines saved before 2026-10-04 (straight from a
 *    truck / part from it). They stay readable; nothing offers them any more.
 * The backend (`wzSourceService`, `wzBatchPickService`) owns the rules. This
 * module only labels the line and keeps the saved choice on a draft edit.
 */
export type WZLineSourceMode = 'FIFO' | 'TRUCK' | 'MIX' | 'BATCHES';

export interface WZLineSourceFields {
  sourceMode?: WZLineSourceMode | null;
  sourceTransportId?: string | null;
  sourcePZDocumentId?: string | null;
  sourceQuantity?: number | null;
}

export interface WZSourceLine extends WZLineSourceFields {
  productVariantId?: string | null;
  quantity?: number | null;
  unit?: string | null;
}

export function getWZLineSourceMode(line: WZLineSourceFields): WZLineSourceMode {
  return line.sourceMode === 'TRUCK' || line.sourceMode === 'MIX' || line.sourceMode === 'BATCHES'
    ? line.sourceMode
    : 'FIFO';
}

/** Legacy truck line (TRUCK / MIX), kept only for WZ saved before 2026-10-04. */
export function isLegacyTruckSource(line: WZLineSourceFields): boolean {
  const mode = getWZLineSourceMode(line);
  return mode === 'TRUCK' || mode === 'MIX';
}

/** Truck part of a legacy line in its own unit (TRUCK = whole line). */
export function getLineTruckQuantity(line: WZSourceLine): number {
  const mode = getWZLineSourceMode(line);
  if (mode === 'TRUCK') return roundWarehouseQuantity(Number(line.quantity) || 0);
  if (mode === 'MIX') return roundWarehouseQuantity(Number(line.sourceQuantity) || 0);
  return 0;
}

/** Another product on the line: back to the warehouse FIFO default. */
export function resetWZLineSourceToFifo(): Required<WZLineSourceFields> {
  return { sourceMode: 'FIFO', sourceTransportId: null, sourcePZDocumentId: null, sourceQuantity: null };
}

/**
 * The four source fields a WZ draft save sends for a line. A legacy truck
 * line round-trips its saved choice (the backend accepts it only for the same
 * line and delivery); a picked line round-trips BATCHES (the backend keeps its
 * batches while id, product, unit and quantity are unchanged, otherwise the
 * line goes back to FIFO); every other line is FIFO.
 */
export function buildWZLineSourcePayload(line: WZSourceLine): Required<WZLineSourceFields> {
  if (getWZLineSourceMode(line) === 'BATCHES') {
    return { sourceMode: 'BATCHES', sourceTransportId: null, sourcePZDocumentId: null, sourceQuantity: null };
  }
  if (!isLegacyTruckSource(line)) return resetWZLineSourceToFifo();
  return {
    sourceMode: getWZLineSourceMode(line),
    sourceTransportId: line.sourceTransportId || null,
    sourcePZDocumentId: line.sourceTransportId ? null : (line.sourcePZDocumentId || null),
    sourceQuantity: getLineTruckQuantity(line),
  };
}
